import React from "react";

import { notifySuccess } from "shared/utils/ui";

import type { AttachmentItem, Persona } from "./types";
import {
  getComparableQty,
  getComparableRunwayMonths,
  getInheritedSyncUpdates,
  getOrphanedDocIds,
  getPersonasWithDataDivergence,
  indexItemsByTypeAndKey,
} from "./utils";

interface UsePersonaInheritanceParams {
  personas: Persona[];
  items: AttachmentItem[];
  values: Record<string, any>;
  onToggleItem: (id: string | number, checked: boolean) => void;
  onChangeQuantity: (id: string | number, quantity: number | null) => void;
  onChangeRunway?: (id: string | number, months: number | null) => void;
  onAddCustomDoc?: (
    name: string,
    applicantTypes?: number[],
    options?: { silent?: boolean }
  ) => Promise<void> | void;
  onDeleteDoc?: (
    ids: (string | number)[],
    options?: { silent?: boolean }
  ) => Promise<void> | void;
}

export const usePersonaInheritance = ({
  personas,
  items,
  values,
  onToggleItem,
  onChangeQuantity,
  onChangeRunway,
  onAddCustomDoc,
  onDeleteDoc,
}: UsePersonaInheritanceParams) => {
  const primaryPersonaIndex = React.useMemo(() => {
    const explicitApplicantIndex = personas.findIndex(
      (persona) => persona.key === "applicant"
    );
    return explicitApplicantIndex >= 0 ? explicitApplicantIndex : 0;
  }, [personas]);
  const [activeTab, setActiveTab] = React.useState(() => primaryPersonaIndex);

  const applicantPersona = personas[primaryPersonaIndex] || personas[0];
  const activePersona = personas[activeTab];

  React.useEffect(() => {
    if (!personas[activeTab]) {
      setActiveTab(primaryPersonaIndex);
    }
  }, [activeTab, personas, primaryPersonaIndex]);

  // Overridden-personas tracking is state-backed so that Customize (adds
  // a key) and Reset (removes a key) are both reflected immediately. The
  // set is seeded once via effect when items first arrive: personas
  // whose persisted items differ from the applicant at mount time start
  // out as Custom. After that initial seed, ONLY explicit user actions
  // (Customize / Reset) mutate the set — we never recompute from items
  // on the fly, because while the applicant is being edited the items
  // prop shows a transient "applicant changed, other personas not yet
  // synced" state that would falsely flip inherited personas to Custom
  // before the sync effect reconciles them.
  const [seededOverriddenPersonas, setSeededOverriddenPersonas] = React.useState<
    Set<string>
  >(new Set());
  const [manualOverriddenPersonas, setManualOverriddenPersonas] = React.useState<
    Set<string>
  >(new Set());
  const overriddenPersonas = React.useMemo(() => {
    const next = new Set(seededOverriddenPersonas);
    manualOverriddenPersonas.forEach((key) => next.add(key));
    return next;
  }, [manualOverriddenPersonas, seededOverriddenPersonas]);
  const overriddenSeedDoneRef = React.useRef(false);
  const [hasSeededOverrides, setHasSeededOverrides] = React.useState(false);
  const hasLoadedItems = items.length > 0;

  // personas / values change every parent render, so pin them to refs
  // and read from inside the effect to keep the seeding one-shot.
  const personasRef = React.useRef(personas);
  personasRef.current = personas;
  const valuesRef = React.useRef(values);
  valuesRef.current = values;
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  React.useEffect(() => {
    if (overriddenSeedDoneRef.current) return;
    if (!hasLoadedItems) return;
    const seed = getPersonasWithDataDivergence(
      personasRef.current,
      itemsRef.current,
      valuesRef.current,
      applicantPersona?.key
    );
    overriddenSeedDoneRef.current = true;
    setSeededOverriddenPersonas(seed);
    // Unblocks inherited sync on the next render so any seeded overrides
    // are applied first. Prevents initial-load races that can flatten
    // divergent persona data back to applicant values.
    setHasSeededOverrides(true);
  }, [hasLoadedItems]);

  // One-shot seed avoids false Custom flips while applicant edits are in
  // flight. But external refetches can legitimately change persona
  // divergence later, so refresh the seeded set only when non-primary rows
  // (or topology) change.
  const topologyFingerprint = React.useMemo(
    () =>
      items
        .map((i) => `${i.id}:${i.applicantType}:${i.isDefault ? 1 : 0}`)
        .join("|"),
    [items]
  );
  const nonPrimaryFingerprint = React.useMemo(() => {
    const primaryType = applicantPersona?.applicantType;
    return items
      .filter((i) => i.applicantType !== primaryType)
      .map((i) => `${i.id}:${i.checked ? 1 : 0}:${getComparableQty(i)}`)
      .join("|");
  }, [applicantPersona?.applicantType, items]);
  const lastReseedDepsRef = React.useRef<{
    nonPrimary: string;
    topology: string;
    toggles: string;
  } | null>(null);

  // Tracks which persona has an in-flight Customize or Reset request so
  // the button shows a loading state and is disabled during the op.
  const [pendingPersonaOp, setPendingPersonaOp] = React.useState<{
    key: string;
    kind: "customize" | "reset";
  } | null>(null);

  // Shared fingerprint — used by the sync effect to dedupe identical updates
  // across renders. id + checked + qty so data-only changes invalidate
  // the cache.
  const itemsFingerprint = React.useMemo(
    () =>
      items
        .map((i) => `${i.id}:${i.checked ? 1 : 0}:${getComparableQty(i)}`)
        .join("|"),
    [items]
  );
  const itemsById = React.useMemo(() => {
    const byId = new Map<string | number, AttachmentItem>();
    items.forEach((item) => byId.set(item.id, item));
    return byId;
  }, [items]);
  const itemsByTypeAndDocKey = React.useMemo(
    () => indexItemsByTypeAndKey(items),
    [items]
  );

  // Sync inherited personas to the applicant. Refs + fingerprint to
  // avoid rerunning on every parent render.
  const onToggleItemRef = React.useRef(onToggleItem);
  onToggleItemRef.current = onToggleItem;
  const onChangeQuantityRef = React.useRef(onChangeQuantity);
  onChangeQuantityRef.current = onChangeQuantity;

  const lastSyncSignatureRef = React.useRef<string>("");
  const togglesFingerprint = React.useMemo(
    () =>
      personas
        .map((p) => `${p.key}:${values[p.toggleField] ? 1 : 0}`)
        .join("|"),
    [personas, values]
  );
  React.useEffect(() => {
    if (!hasSeededOverrides) return;
    const prev = lastReseedDepsRef.current;
    const next = {
      nonPrimary: nonPrimaryFingerprint,
      topology: topologyFingerprint,
      toggles: togglesFingerprint,
    };
    if (!prev) {
      lastReseedDepsRef.current = next;
      return;
    }
    const nonPrimaryChanged = prev.nonPrimary !== next.nonPrimary;
    const topologyChanged = prev.topology !== next.topology;
    const togglesChanged = prev.toggles !== next.toggles;
    if (!nonPrimaryChanged && !topologyChanged && !togglesChanged) return;
    lastReseedDepsRef.current = next;
    const reseeded = getPersonasWithDataDivergence(
      personas,
      items,
      values,
      applicantPersona?.key
    );
    setSeededOverriddenPersonas(reseeded);
  }, [
    applicantPersona?.key,
    hasSeededOverrides,
    items,
    nonPrimaryFingerprint,
    personas,
    togglesFingerprint,
    topologyFingerprint,
    values,
  ]);

  const onChangeRunwayRef = React.useRef(onChangeRunway);
  onChangeRunwayRef.current = onChangeRunway;

  React.useEffect(() => {
    if (items.length === 0) return;
    if (!hasSeededOverrides) return;
    // Combine the persisted overridden set with personas that diverge
    // RIGHT NOW. This closes the re-seed/auto-sync race: when items
    // refetch with new divergent rows, the seeded set hasn't caught up
    // yet but the live divergence is visible in `items`. Without this
    // union, the sync would push applicant data onto a persona that's
    // about to be marked Custom — overwriting the divergence before
    // the user sees it.
    const liveDivergent = getPersonasWithDataDivergence(
      personas,
      items,
      values,
      applicantPersona?.key
    );
    const effectiveOverrides = new Set(overriddenPersonas);
    liveDivergent.forEach((k) => effectiveOverrides.add(k));
    const updates = getInheritedSyncUpdates(
      personas,
      items,
      effectiveOverrides,
      values,
      applicantPersona?.key
    );
    if (updates.length === 0) return;
    // Signature dedupes in-flight updates before parent state catches up.
    const signature = updates
      .map((u) => `${u.id}:${u.checked ? 1 : 0}:${u.qty}:${u.runwayMonths}`)
      .join("|");
    if (signature === lastSyncSignatureRef.current) return;
    lastSyncSignatureRef.current = signature;
    updates.forEach(({ id, checked, qty, runwayMonths }) => {
      const current = itemsById.get(id);
      if (!current) return;
      if (!!current.checked !== checked) {
        onToggleItemRef.current(id, checked);
      }
      const currentQty = getComparableQty(current);
      if (currentQty !== qty) {
        onChangeQuantityRef.current(id, qty);
      }
      if (onChangeRunwayRef.current) {
        const currentRunway = getComparableRunwayMonths(current);
        if (currentRunway !== runwayMonths) {
          onChangeRunwayRef.current(id, runwayMonths);
        }
      }
    });
  }, [
    hasSeededOverrides,
    items,
    itemsFingerprint,
    itemsById,
    overriddenPersonas,
    togglesFingerprint,
    personas,
    values,
  ]);

  const syncFromApplicant = React.useCallback(
    ({ silent = false }: { silent?: boolean } = {}) => {
      if (!activePersona || !applicantPersona) return Promise.resolve();
      const applicantItemsByDocKey =
        itemsByTypeAndDocKey.get(applicantPersona.applicantType) || new Map();
      const activeItemsByDocKey =
        itemsByTypeAndDocKey.get(activePersona.applicantType) || new Map();
      const missingCustomDocs = new Set<string>();
      applicantItemsByDocKey.forEach((appItem, docKey) => {
        const matchingItem = activeItemsByDocKey.get(docKey);
        if (matchingItem) {
          if (!!appItem.checked !== !!matchingItem.checked) {
            onToggleItem(matchingItem.id, !!appItem.checked);
          }
          const appQty = getComparableQty(appItem);
          const curQty = getComparableQty(matchingItem);
          if (appQty !== curQty) {
            onChangeQuantity(matchingItem.id, appQty);
          }
          if (onChangeRunway) {
            const appRunway = getComparableRunwayMonths(appItem);
            const curRunway = getComparableRunwayMonths(matchingItem);
            if (appRunway !== curRunway) {
              onChangeRunway(matchingItem.id, appRunway);
            }
          }
        } else if (!appItem.isDefault && onAddCustomDoc) {
          missingCustomDocs.add(appItem.genericName || appItem.label);
        }
      });
      if (missingCustomDocs.size === 0 || !onAddCustomDoc) {
        return Promise.resolve();
      }
      const targetType = activePersona.applicantType;
      return Promise.all(
        Array.from(missingCustomDocs).map((name) =>
          onAddCustomDoc(name, [targetType], { silent })
        )
      );
    },
    [
      activePersona,
      applicantPersona,
      itemsByTypeAndDocKey,
      onAddCustomDoc,
      onChangeQuantity,
      onChangeRunway,
      onToggleItem,
    ]
  );

  const handleCustomize = React.useCallback(async () => {
    if (!activePersona) return;
    setPendingPersonaOp({ key: activePersona.key, kind: "customize" });
    try {
      await syncFromApplicant({ silent: true });
    } catch {
      setPendingPersonaOp(null);
      return;
    }
    setManualOverriddenPersonas((prev) => new Set(prev).add(activePersona.key));
    setPendingPersonaOp(null);
  }, [activePersona, syncFromApplicant]);

  const handleResetToApplicant = React.useCallback(async () => {
    if (!activePersona || !applicantPersona) return;
    setPendingPersonaOp({ key: activePersona.key, kind: "reset" });
    let orphansDeleted = false;
    try {
      if (onDeleteDoc) {
        const orphanedIds = getOrphanedDocIds(
          activePersona.applicantType,
          applicantPersona.applicantType,
          items
        );
        if (orphanedIds.length > 0) {
          await onDeleteDoc(orphanedIds, { silent: true });
          orphansDeleted = true;
        }
      }
      await syncFromApplicant({ silent: true });
    } catch {
      setPendingPersonaOp(null);
      return;
    }
    setManualOverriddenPersonas((prev) => {
      const next = new Set(prev);
      next.delete(activePersona.key);
      return next;
    });
    setSeededOverriddenPersonas((prev) => {
      const next = new Set(prev);
      next.delete(activePersona.key);
      return next;
    });
    // Toast only when a server-side mutation occurred — the form-state
    // sync doesn't persist until Save.
    if (orphansDeleted) {
      notifySuccess(`Removed custom documents for ${activePersona.label}`);
    }
    setPendingPersonaOp(null);
  }, [activePersona, applicantPersona, items, onDeleteDoc, syncFromApplicant]);

  const isApplicant = activePersona?.key === applicantPersona?.key;
  const isOverridden = overriddenPersonas.has(activePersona?.key);
  const isSynced = !isApplicant && !isOverridden;
  const isEditable = isApplicant || isOverridden;

  return {
    activePersona,
    activeTab,
    applicantPersona,
    handleCustomize,
    handleResetToApplicant,
    isApplicant,
    isEditable,
    isOverridden,
    isSynced,
    itemsByTypeAndDocKey,
    overriddenPersonas,
    pendingPersonaOp,
    primaryPersonaIndex,
    setActiveTab,
  };
};
